import { MultiSelectOutlined } from '@amelio/ui-library';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SectionHeading from '../SectionHeading';

import type { ObjectiveFormData } from '../../validation';
import type { Control } from 'react-hook-form';

import { useGetEmployeeCompetencies } from '@/api/cycle-evaluation-services/manager-assessment';

interface ObjectiveCompetenciesFieldProps {
    control: Control<ObjectiveFormData>;
    /** The individual objective's owner, whose competencies are offered. */
    ownerId?: string;
}

const toggle = (values: string[], value: string): string[] =>
    values.includes(value) ? values.filter((current) => current !== value) : [...values, value];

/** The owner's active competencies an individual objective develops. */
export default function ObjectiveCompetenciesField({
    control,
    ownerId,
}: ObjectiveCompetenciesFieldProps) {
    const { t } = useTranslation();
    const { data: competencies = [], isLoading: areCompetenciesLoading } =
        useGetEmployeeCompetencies(ownerId);
    const selectableCompetencies = competencies.filter((competency) => !competency.isDeactivated);

    return (
        <div
            className={'flex flex-col gap-6'}
            data-testid={'objective-form-competencies-card'}
        >
            <SectionHeading
                icon={'school-award'}
                title={t('objectives.form.competencies')}
            />

            <Controller
                name={'competencyIds'}
                control={control}
                render={({ field }) => (
                    <MultiSelectOutlined
                        options={selectableCompetencies.map((competency) => ({
                            id: competency.id,
                            value: competency.id,
                            label: competency.name,
                        }))}
                        values={field.value}
                        onChange={(value) => field.onChange(toggle(field.value, value))}
                        disabled={!ownerId}
                        showSelection
                        emptyText={t('common.noResults')}
                        ariaLabel={t('objectives.form.competencies')}
                        dataTestId={'objective-form-competencies'}
                    />
                )}
            />

            {!ownerId && (
                <p className={'text-obj-muted text-[14px]'}>
                    {t('objectives.form.selectEmployeeFirst')}
                </p>
            )}

            {/* The list is empty while it is being fetched too, so "this employee has no
                competency" waits for the answer rather than greeting every pick. */}
            {!!ownerId && !areCompetenciesLoading && selectableCompetencies.length === 0 && (
                <p className={'text-obj-muted text-[14px]'}>
                    {t('objectives.form.noCompetencies')}
                </p>
            )}
        </div>
    );
}
